import { useMemo, useState } from 'react'
import { Box, Chip } from '@mui/material'
import { Panel } from 'Common/Components/Panel'
import { RunStatusChip } from 'Common/Components/RunStatusChip'
import { SegmentedControl } from 'Common/Components/SegmentedControl'
import { methodLabel } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { MethodComparison } from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'
import {
  busiestWeekHours,
  TemplateTimeline,
  TimelineLegend,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/Results/TemplateTimeline'

type ChartMode = 'sequenced' | 'totalled'

/**
 * Each method's week-by-week sequence of events, side by side on one hours
 * scale, so the same week lines up across methods and a fuller week is
 * visibly longer. Hovering a SKU group picks it out in every method. One
 * legend for all; a method that hasn't finished keeps its column with its
 * status, so the columns don't shift as runs complete.
 */
export const MethodSequence = ({
  methods,
  preferred,
}: {
  methods: MethodComparison['methods']
  preferred: string
}) => {
  const [mode, setMode] = useState<ChartMode>('sequenced')
  const [focusSku, setFocusSku] = useState<string | null>(null)

  const capacityOf = (m: MethodComparison['methods'][number]) =>
    m.summary?.workcentreCapacityInitial ?? 0

  const maxHours = useMemo(
    () =>
      Math.max(
        0,
        ...methods.map((m) =>
          Math.max(capacityOf(m), busiestWeekHours(m.outputs))
        )
      ),
    [methods]
  )

  return (
    <Panel
      title="Sequence of events"
      action={
        <SegmentedControl
          label="Chart view"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'sequenced', label: 'Sequenced' },
            { value: 'totalled', label: 'Totalled' },
          ]}
        />
      }
    >
      <TimelineLegend />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            xl: `repeat(${methods.length}, minmax(0, 1fr))`,
          },
          gap: '16px',
        }}
      >
        {methods.map((m) => {
          const ready = m.status === 'completed' && m.summary
          return (
            <Box key={m.templateMethod} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: '6px',
                  minHeight: 24,
                }}
              >
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink-900)',
                  }}
                >
                  {methodLabel(m.templateMethod)}
                </Box>
                {m.templateMethod === preferred && (
                  <Chip label="Preferred" color="primary" />
                )}
                {!ready && <RunStatusChip status={m.status} />}
                {ready && (
                  <Box
                    sx={{
                      ml: 'auto',
                      fontSize: 11.5,
                      color: 'var(--ink-500)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    capacity {capacityOf(m).toFixed(1)}h
                  </Box>
                )}
              </Box>
              {ready ? (
                <TemplateTimeline
                  outputs={m.outputs}
                  capacity={capacityOf(m)}
                  mode={mode}
                  maxHours={maxHours}
                  showLegend={false}
                  focusSku={focusSku}
                  onFocusSku={setFocusSku}
                />
              ) : (
                <Box
                  sx={{
                    py: '32px',
                    textAlign: 'center',
                    fontSize: 12,
                    color: 'var(--ink-500)',
                    border: '1px dashed var(--line-200)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  No template for this method yet.
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Panel>
  )
}
